import type { AccountingCode } from 'types/entities/accounting/accountingCode.type';
import type { TaxRate } from 'types/entities/accounting/taxRate.type';
import type { OcrProduct, OcrTaxLine } from 'composables/ocr/types';

export const DEFAULT_AMOUNT_DECIMALS = 2;
export const E_INVOICING_AMOUNT_DECIMALS = 2;

export const resolveAmountDecimals = (isElectronic: boolean, companyDecimals: number): number =>
    isElectronic ? E_INVOICING_AMOUNT_DECIMALS : companyDecimals;

export const precisionFactor = (decimals: number): number => 10 ** decimals;

const toFloat = (value: number | string | null | undefined): number => {
    const normalized = String(value ?? '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const round = (value: number, decimals: number): number => {
    const factor = precisionFactor(decimals);
    return Math.round(value * factor) / factor;
};

export const parseAmountToNumber = (value: number | string | null | undefined): number =>
    toFloat(value);

export const ocrTotalDiffersFromLineTotal = (
    ocrTotal: number | string | null | undefined,
    lineTotal: number,
    decimals: number,
): boolean => {
    if (ocrTotal === null || ocrTotal === undefined || ocrTotal === '') {
        return false;
    }
    return round(parseAmountToNumber(ocrTotal), decimals) !== round(lineTotal, decimals);
};

const parseQuantity = (quantity: number | string | null | undefined): number => {
    if (!quantity) {
        return 1;
    }
    const parsed = typeof quantity === 'number' ? quantity : parseFloat(quantity);
    return Number.isNaN(parsed) ? 1 : parsed;
};

const computeTaxAmountForRate = (
    products: OcrProduct[],
    taxId: number,
    taxRate: string | number,
    decimals: number,
): number => {
    const rate = Number(taxRate);
    const factor = precisionFactor(decimals);

    const totalWithoutTaxInCents = products
        .filter((product) => product.taxRateId === taxId)
        .reduce((sum, product) => {
            const qty = parseQuantity(product.quantity);
            const priceWithoutTax = toFloat(product.priceWithoutTax || 0);
            return sum + Math.round(priceWithoutTax * qty * factor);
        }, 0);

    return Math.round((totalWithoutTaxInCents * rate) / 100);
};

export const calculateTotalWithoutTax = (
    products: OcrProduct[] | undefined,
    decimals: number,
): number => {
    const safeProducts = products ?? [];
    const factor = precisionFactor(decimals);
    const total = safeProducts.reduce((sum, { priceWithoutTax, quantity }) => {
        const qty = parseQuantity(quantity);
        const amount = toFloat(priceWithoutTax);
        const amountInCents = Math.round(amount * qty * factor);
        return sum + amountInCents / factor;
    }, 0);

    return round(total, decimals);
};

export const calculateTotalWithTax = (
    totalWithoutTax: number | null,
    totalTax: number | null,
    decimals: number,
): number => {
    const factor = precisionFactor(decimals);
    const withoutTaxInCents = Math.round(toFloat(totalWithoutTax) * factor);
    const taxInCents = Math.round(toFloat(totalTax) * factor);

    return round((withoutTaxInCents + taxInCents) / factor, decimals);
};

export const calculateAmountIncludingTax = (
    amountExcludingTax: number | string | null | undefined,
    rate: number,
): number | null => {
    const parsed = parseFloat(String(amountExcludingTax ?? '').replace(',', '.'));
    if (Number.isNaN(parsed)) {
        return null;
    }
    const factor = precisionFactor(DEFAULT_AMOUNT_DECIMALS);
    const exclInCents = Math.round(parsed * factor);
    const taxInCents = Math.round((exclInCents * rate) / 100);
    return round((exclInCents + taxInCents) / factor, DEFAULT_AMOUNT_DECIMALS);
};

export const calculateTaxLinesTotalWithoutTax = (taxLines: OcrTaxLine[] | undefined): number => {
    const safeTaxLines = taxLines ?? [];
    const factor = precisionFactor(DEFAULT_AMOUNT_DECIMALS);
    const totalInCents = safeTaxLines.reduce(
        (sum, { amountExcludingTax }) => sum + Math.round(toFloat(amountExcludingTax) * factor),
        0,
    );
    return round(totalInCents / factor, DEFAULT_AMOUNT_DECIMALS);
};

export const calculateTaxLinesTotalWithTax = (taxLines: OcrTaxLine[] | undefined): number => {
    const safeTaxLines = taxLines ?? [];
    const factor = precisionFactor(DEFAULT_AMOUNT_DECIMALS);
    const totalInCents = safeTaxLines.reduce(
        (sum, { amountIncludingTax }) => sum + Math.round(toFloat(amountIncludingTax) * factor),
        0,
    );
    return round(totalInCents / factor, DEFAULT_AMOUNT_DECIMALS);
};

export const calculateTaxLinesTotalTax = (taxLines: OcrTaxLine[]): number => {
    const factor = precisionFactor(DEFAULT_AMOUNT_DECIMALS);
    const totalTaxInCents =
        Math.round(calculateTaxLinesTotalWithTax(taxLines) * factor) -
        Math.round(calculateTaxLinesTotalWithoutTax(taxLines) * factor);
    return round(totalTaxInCents / factor, DEFAULT_AMOUNT_DECIMALS);
};

export const calculateTotalTax = (
    products: OcrProduct[] | undefined,
    taxRows: TaxRate[],
    decimals: number,
): number => {
    const safeProducts = products ?? [];
    const factor = precisionFactor(decimals);
    let overrideTaxInCents = 0;
    const nonOverriddenProducts: OcrProduct[] = [];

    for (const product of safeProducts) {
        if (product.taxAmountOverride !== null && product.taxAmountOverride !== undefined) {
            const parsed = parseFloat(String(product.taxAmountOverride).replace(',', '.'));
            if (!Number.isNaN(parsed)) {
                overrideTaxInCents += Math.round(parsed * factor);
                continue;
            }
        }
        nonOverriddenProducts.push(product);
    }

    const rateTaxInCents = taxRows.reduce((sum, taxItem) => {
        return (
            sum + computeTaxAmountForRate(nonOverriddenProducts, taxItem.id, taxItem.rate, decimals)
        );
    }, 0);

    return round((overrideTaxInCents + rateTaxInCents) / factor, decimals);
};

export const getUniqueTaxRows = (
    products: OcrProduct[] | undefined,
    taxList: TaxRate[],
): TaxRate[] => {
    const safeProducts = products ?? [];
    const seenIds = new Set<number>();
    const taxes: TaxRate[] = [];

    for (const product of safeProducts) {
        if (
            product.taxRateId === null ||
            product.taxRateId === undefined ||
            seenIds.has(product.taxRateId)
        ) {
            continue;
        }
        const taxItem = taxList.find((tax) => tax.id === product.taxRateId);
        if (taxItem) {
            seenIds.add(taxItem.id);
            taxes.push(taxItem);
        }
    }

    return taxes;
};

export const calculateTotalByTax = (
    products: OcrProduct[],
    taxId: number,
    taxRate: string | number,
    decimals: number,
): number => {
    const factor = precisionFactor(decimals);
    let overrideInCents = 0;
    const nonOverridden: OcrProduct[] = [];

    for (const product of products) {
        if (product.taxRateId !== taxId) {
            continue;
        }
        if (product.taxAmountOverride !== null && product.taxAmountOverride !== undefined) {
            const parsed = parseFloat(String(product.taxAmountOverride).replace(',', '.'));
            if (!Number.isNaN(parsed)) {
                overrideInCents += Math.round(parsed * factor);
                continue;
            }
        }
        nonOverridden.push(product);
    }

    return round(
        (overrideInCents + computeTaxAmountForRate(nonOverridden, taxId, taxRate, decimals)) /
            factor,
        decimals,
    );
};

const DEFAULT_EMPTY_PRODUCT: Omit<OcrProduct, 'accountingCode' | 'taxRateId'> = {
    description: '',
    quantity: 1,
    priceWithoutTax: null,
    unit: '',
    catalogueLink: null,
    analyticCode: '',
    stockOutDate: null,
    warehouseId: '',
    serialNumbers: '',
    taxAmountOverride: null,
    amountIncludingTaxOverride: null,
};

export interface CreateEmptyProductOptions {
    defaultAccountingCode?: number | null;
    defaultTaxRateId?: number | null;
    supplierId?: number | null;
    companyAccountingCode?: number | null;
}

export const createEmptyProduct = ({
    defaultAccountingCode = null,
    defaultTaxRateId = null,
    supplierId = null,
    companyAccountingCode = null,
}: CreateEmptyProductOptions = {}): OcrProduct => ({
    ...DEFAULT_EMPTY_PRODUCT,
    accountingCode: supplierId === null ? defaultAccountingCode : companyAccountingCode,
    taxRateId: defaultTaxRateId,
});

const DEFAULT_EMPTY_TAX_LINE: Omit<OcrTaxLine, 'accountingCode' | 'taxRateId'> = {
    description: '',
    unit: '',
    analyticCode: '',
    amountExcludingTax: null,
    amountIncludingTax: null,
};

export interface CreateEmptyTaxLineOptions {
    defaultAccountingCode?: number | null;
    defaultTaxRateId?: number | null;
}

export const createEmptyTaxLine = ({
    defaultAccountingCode = null,
    defaultTaxRateId = null,
}: CreateEmptyTaxLineOptions = {}): OcrTaxLine => ({
    ...DEFAULT_EMPTY_TAX_LINE,
    accountingCode: defaultAccountingCode,
    taxRateId: defaultTaxRateId,
});

export const limitToDecimals = (
    value: string | number | null,
    maxDecimals: number,
): string | number | null => {
    if (value === null || value === '' || value === '-') {
        return value;
    }
    const str = String(value);
    const separator = str.includes(',') ? ',' : '.';
    const separatorIndex = str.indexOf(separator);
    if (separatorIndex === -1) {
        return str;
    }
    const cutIndex = maxDecimals > 0 ? separatorIndex + 1 + maxDecimals : separatorIndex;
    return str.length > cutIndex ? str.substring(0, cutIndex) : str;
};

export const limitToTwoDecimals = (value: string | number | null): string | number | null =>
    limitToDecimals(value, DEFAULT_AMOUNT_DECIMALS);

/**
 * Formats an amount value to exactly 2 decimal places with comma separator.
 * Returns the value unchanged when it is null, empty, or non-numeric.
 */
export const formatAmountToTwoDecimals = (
    value: string | number | null,
): string | number | null => {
    if (value === null || value === '') {
        return value;
    }
    const parsed = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    return Number.isNaN(parsed) ? value : parsed.toFixed(2).replace('.', ',');
};

/**
 * Formats a field value to exactly 2 decimal places with comma separator.
 */
export const formatFieldToTwoDecimals = <
    TKey extends string,
    TObject extends { [P in TKey]: string | number | null },
>(
    object: TObject,
    field: TKey,
): void => {
    const value = object[field];
    if (value !== null && value !== '') {
        const parsed =
            typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
        if (!Number.isNaN(parsed)) {
            (object as { [P in TKey]: string })[field] = parsed.toFixed(2).replace('.', ',');
        }
    }
};

export const setProductDescriptionFromAccountingCode = (
    product: OcrProduct,
    accountingCodes: AccountingCode[],
): void => {
    const hasCustomDescription =
        product.description && !accountingCodes.some((code) => code.label === product.description);
    if (hasCustomDescription) {
        return;
    }

    const matchingCode = accountingCodes.find((code) => code.id === product.accountingCode);
    product.description = matchingCode?.label ?? '';
};
